import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Seo } from "@/components/Seo";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { API, errMsg } from "@/lib/api";

export default function AdminSetPassword() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [checking, setChecking] = useState(true);
  const [pw, setPw] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    API.get("/auth/me")
      .then((r) => {
        setUser(r.data);
        if (!r.data.must_change_password) navigate("/admin", { replace: true });
      })
      .catch(() => navigate("/admin/login", { replace: true }))
      .finally(() => setChecking(false));
  }, [navigate]);

  const submit = async (e) => {
    e.preventDefault();
    if (pw !== confirm) return toast.error("Passwords don't match");
    setBusy(true);
    try {
      await API.post("/auth/set-password", { new_password: pw });
      toast.success("Password set — welcome to the admin panel");
      navigate("/admin", { replace: true });
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  if (checking || !user) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="h-8 w-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-muted/40 px-4" data-testid="set-password-page">
      <Seo title="Set Your Password | Eileen Jenkins Florist" />
      <Card className="w-full max-w-md">
        <CardContent className="p-8">
          <p className="font-display text-2xl text-center text-primary">Choose your password</p>
          <p className="text-sm text-muted-foreground text-center mt-2 mb-8">
            Hi {user.name || user.email} — for security, please set your own password before continuing.
          </p>
          <form onSubmit={submit} className="space-y-5" data-testid="set-password-form">
            <div className="space-y-2">
              <Label htmlFor="sp-new">New password (min 8 characters)</Label>
              <Input id="sp-new" type="password" required minLength={8} value={pw} onChange={(e) => setPw(e.target.value)} data-testid="set-password-input" autoComplete="new-password" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="sp-confirm">Confirm password</Label>
              <Input id="sp-confirm" type="password" required minLength={8} value={confirm} onChange={(e) => setConfirm(e.target.value)} data-testid="set-password-confirm-input" autoComplete="new-password" />
            </div>
            <Button type="submit" className="w-full rounded-full" disabled={busy || pw.length < 8 || pw !== confirm} data-testid="set-password-submit-btn">
              {busy ? "Saving…" : "Set password & continue"}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
